import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { ArrowLeft, Loader2, PartyPopper } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useCampaignList,
  useNextCampaignContacts,
  useUpdateCampaignContact,
  type CampaignContact,
} from '../api/campaigns'
import { CampaignContactCard } from '../components/campaigns/CampaignContactCard'
import { CampaignProgress } from '../components/campaigns/CampaignProgress'
import { CampaignUpcomingQueue } from '../components/campaigns/CampaignUpcomingQueue'
import { CampaignCallHistory } from '../components/campaigns/CampaignCallHistory'
import { DispositionPicker } from '../components/campaigns/DispositionPicker'
import { CallStatusPanel } from '../components/call-staging/CallStatusPanel'
import { useActiveCallForPhone } from '../components/phone/useActiveCallForPhone'

type Phase = 'loading' | 'ready' | 'calling' | 'disposition' | 'complete'

export function CampaignRunnerPage() {
  const { t } = useTranslation('campaigns')
  const { id } = useParams()
  const listId = id ? Number(id) : undefined
  const navigate = useNavigate()

  const { data: campaign, refetch: refetchCampaign } = useCampaignList(listId)
  const { data: nextData, refetch: refetchNext } = useNextCampaignContacts(listId, 6)
  const updateContact = useUpdateCampaignContact()

  const [currentContact, setCurrentContact] = useState<CampaignContact | null>(null)
  const [contactIndex, setContactIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>('loading')

  const activeCall = useActiveCallForPhone(currentContact?.phone ?? '')

  // Load first contact when data arrives
  useEffect(() => {
    if (!nextData || currentContact) return
    if (nextData.contacts.length > 0) {
      // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
      // It has multiple writers (this effect plus user event handlers), so deriving
      // it during render is a state-machine redesign, not a mechanical fix — and the
      // UI has no JS test suite to catch a regression. Suppressed, not fixed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentContact(nextData.contacts[0])
      setContactIndex((campaign?.stats.contacted ?? 0) + 1)
      setPhase('ready')
    } else if (campaign && campaign.stats.remaining === 0 && campaign.stats.total > 0) {
      setPhase('complete')
    }
  }, [nextData, currentContact, campaign])

  // Detect call started → transition to calling phase
  useEffect(() => {
    if (activeCall && phase === 'ready') {
      // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
      // It has multiple writers (this effect plus user event handlers), so deriving
      // it during render is a state-machine redesign, not a mechanical fix — and the
      // UI has no JS test suite to catch a regression. Suppressed, not fixed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPhase('calling')
    }
  }, [activeCall, phase])

  // Detect call ended → transition to disposition phase
  useEffect(() => {
    if (!activeCall && phase === 'calling') {
      // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
      // It has multiple writers (this effect plus user event handlers), so deriving
      // it during render is a state-machine redesign, not a mechanical fix — and the
      // UI has no JS test suite to catch a regression. Suppressed, not fixed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPhase('disposition')
    }
  }, [activeCall, phase])

  const advanceToNext = useCallback(async () => {
    setCurrentContact(null)
    setPhase('loading')
    await refetchCampaign()
    await refetchNext()
  }, [refetchCampaign, refetchNext])

  const handleDisposition = async (disposition: string, notes: string) => {
    if (!currentContact) return
    await updateContact.mutateAsync({
      contactId: currentContact.id,
      status: 'called',
      disposition,
      notes,
      called_at: Date.now() / 1000,
    })
    advanceToNext()
  }

  const handleSkip = async () => {
    if (!currentContact) return
    await updateContact.mutateAsync({
      contactId: currentContact.id,
      status: 'skipped',
    })
    advanceToNext()
  }

  if (!listId) {
    navigate('/app/campaigns')
    return null
  }

  return (
    <div className="space-y-5">
      {/* Back link */}
      <Link
        to={`/app/campaigns/${id}`}
        className="inline-flex items-center gap-1.5 text-sm text-text-secondary transition-colors hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('runner.back')}
      </Link>

      {/* Progress header */}
      {campaign && (
        <CampaignProgress name={campaign.name} stats={campaign.stats} />
      )}

      {/* Loading state */}
      {phase === 'loading' && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Ready / Calling / Disposition states */}
      {currentContact && phase !== 'loading' && (
        <div className="space-y-4">
          {/* Contact card */}
          <CampaignContactCard
            contact={currentContact}
            index={contactIndex}
            total={campaign?.stats.total ?? 0}
          />

          {/* Call controls — reused from call staging */}
          <CallStatusPanel phoneNumber={currentContact.phone} />

          {/* Disposition picker — shown after call ends */}
          {phase === 'disposition' && (
            <DispositionPicker
              onSave={handleDisposition}
              onSkip={handleSkip}
              isSubmitting={updateContact.isPending}
            />
          )}
        </div>
      )}

      {/* Campaign complete */}
      {phase === 'complete' && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-surface-card py-16">
          <PartyPopper className="h-12 w-12 text-success" />
          <h2 className="mt-4 text-xl font-bold text-text-primary">{t('runner.complete')}</h2>
          <p className="mt-2 text-sm text-text-secondary">
            {t('runner.completeBody', { count: campaign?.stats.total ?? 0 })}
          </p>
          <Link
            to={`/app/campaigns/${id}`}
            className="mt-6 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            {t('runner.backToCampaign')}
          </Link>
        </div>
      )}

      {/* Upcoming calls queue */}
      {nextData && phase !== 'complete' && (
        <CampaignUpcomingQueue
          contacts={nextData.contacts}
          currentContactId={currentContact?.id}
        />
      )}

      {/* Call history */}
      {listId && (
        <CampaignCallHistory listId={listId} />
      )}
    </div>
  )
}
